import { httpClient, mutationEndpoint, queryEndpoint } from '../system';

export enum CandidateRoleCategory {
  Main = 'Main',
  Qualifying = 'Qualifying',
  Other = 'Other',
}

export enum CandidateRoleInterviewType {
  ScreeningCall = 'ScreeningCall',
  HardSkillsInterview = 'HardSkillsInterview',
}

// NOTE: these types mirror the SANITIZED view-model produced by the
// pages/api/roles proxy, not the raw CRM payload. Vetting steps,
// Pass/Reject outcomes, application stage, and booking ineligibility reasons
// are mapped/stripped server-side and must never reach the browser.
export type CandidateRoleInterviewBooking = {
  canRequestBookingLink: boolean;
  nextInterviewType: CandidateRoleInterviewType | null;
};

export enum CandidateRoleTestStatus {
  NotStarted = 'not_started',
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed',
  Skipped = 'skipped',
}

export enum CandidateRoleCallStatus {
  NotStarted = 'not_started',
  InProgress = 'in_progress',
  Completed = 'completed',
  Skipped = 'skipped',
}

export enum CandidateRoleInterviewStatus {
  NotBooked = 'not_booked',
  Booked = 'booked',
  Completed = 'completed',
}

export enum CandidateRoleScreeningStatus {
  None = 'none',
  Pending = 'pending',
  Booked = 'booked',
  Completed = 'completed',
  Skipped = 'skipped',
}

export type CandidateRoleAssessment = {
  test: {
    status: CandidateRoleTestStatus;
    allowRetake: boolean;
    link: string | null;
  };
  call: {
    status: CandidateRoleCallStatus;
    errored: boolean;
    allowRetake: boolean;
  };
  interview: {
    status: CandidateRoleInterviewStatus;
    bookedAt: string | null;
    link: string | null;
  };
  screening: {
    status: CandidateRoleScreeningStatus;
    bookedAt: string | null;
    link: string | null;
  };
};

export enum CandidateRoleReviewStatus {
  None = 'none',
  Pending = 'pending',
  Passed = 'passed',
  Waitlisted = 'waitlisted',
}

export enum CandidateRoleVerificationOutcome {
  None = 'none',
  Failed = 'failed',
  Verified = 'verified',
}

export enum CandidateRoleSeniority {
  Junior = 'Junior',
  JuniorToMiddle = 'Junior-to-middle',
  Middle = 'Middle',
  MiddleToSenior = 'Middle-to-senior',
  Senior = 'Senior',
  StrongSenior = 'Strong senior',
}

export type CandidateRoleReview = {
  status: CandidateRoleReviewStatus;
};

export type CandidateRoleVerification = {
  outcome: CandidateRoleVerificationOutcome;
  seniority: CandidateRoleSeniority | null;
};

export type CandidateRoleRecord = {
  id: string;
  role: string;
  humanReadableRole: string;
  pickable: boolean;
  isMain?: boolean;
  isVerified?: boolean;
  roleCategory?: CandidateRoleCategory;
  paused: boolean;
  onHold?: boolean;
  review?: CandidateRoleReview;
  verification?: CandidateRoleVerification;
  started: boolean;
  assessment: CandidateRoleAssessment;
  interviewBooking: CandidateRoleInterviewBooking;
};

export type CandidateRolesResponse = {
  maxRoles: number | null;
  pickedCount: number;
  hasActiveTest: boolean;
  roles: CandidateRoleRecord[];
};

export type CandidateContactRole = {
  id: string;
  role: string;
  humanReadableRole?: string | null;
};

export type CandidateInterviewBookingLink = {
  link: string | null;
};

const getCandidateRoles = queryEndpoint({
  entity: 'candidateRoles',
  queryKey: (apiKey?: string) => ['candidate-roles', apiKey ?? ''],
  queryFn: (apiKey?: string, nextContext?) =>
    httpClient.auto<CandidateRolesResponse>({
      url: '/api/roles',
      method: 'GET',
      headers: {
        'auth-key': apiKey,
      },
      nextContext,
    }),
  defaultValue: {
    maxRoles: 0,
    pickedCount: 0,
    hasActiveTest: false,
    roles: [],
  } as CandidateRolesResponse,
});

const addRole = mutationEndpoint({
  actionName: 'addRole',
  mutationKey: ['add-role'],
  mutationFn: ({ apiKey, role }: { apiKey: string; role: string }) =>
    httpClient.auto<CandidateContactRole>({
      url: '/api/roles',
      method: 'POST',
      headers: {
        'auth-key': apiKey,
      },
      data: { role },
    }),
});

const createInterviewBookingLink = mutationEndpoint({
  actionName: 'createInterviewBookingLink',
  mutationKey: ['create-interview-booking-link'],
  mutationFn: ({
    apiKey,
    contactRoleId,
  }: {
    apiKey: string;
    contactRoleId: string;
  }) =>
    httpClient.auto<CandidateInterviewBookingLink>({
      url: `/api/roles/${encodeURIComponent(
        contactRoleId
      )}/interview-booking-link`,
      method: 'POST',
      headers: {
        'auth-key': apiKey,
      },
    }),
});

export { addRole, createInterviewBookingLink, getCandidateRoles };
